/**
 * Job Dashboard Component
 * Complete dashboard view combining all job analytics components
 * Demonstrates real-time data updates and component integration
 */

import { useState, useEffect } from 'react';
import { DiversionGauge } from './DiversionGauge';
import { MaterialBreakdownChart } from './MaterialBreakdownChart';
import { JobStats } from './JobStats';
import { fetchJobDiversionStats, fetchJobMaterialSummary } from '../../api/jobs';
import type { JobDiversionStats, MaterialSummary } from '../../types/job';
import { useAuth } from '../../contexts/AuthContext';
import { RegisterPromptDialog } from '../auth/RegisterPromptDialog';

interface JobDashboardProps {
  jobId: string;
  refreshInterval?: number; // milliseconds, default 30s
  onRefresh?: () => void; // callback when data refreshes
}

export function JobDashboard({
  jobId,
  refreshInterval = 30000,
  onRefresh,
}: JobDashboardProps) {
  const [stats, setStats] = useState<JobDiversionStats | null>(null);
  const [materials, setMaterials] = useState<MaterialSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const { isAuthenticated } = useAuth();

  const loadData = async () => {
    try {
      setError(null);
      const [diversionStats, summaryData] = await Promise.all([
        fetchJobDiversionStats(jobId),
        fetchJobMaterialSummary(jobId),
      ]);

      setStats(diversionStats);
      setMaterials(summaryData.summary);
      setLoading(false);
      onRefresh?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job data');
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Auto-refresh
  useEffect(() => {
    if (!refreshInterval) return;

    const interval = setInterval(loadData, refreshInterval);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, refreshInterval]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-2 border-red-200 bg-red-50">
        <div className="flex items-center gap-3">
          <svg
            className="w-6 h-6 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-red-900">
              Failed to load job data
            </h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={loadData}
          className="mt-4 btn-primary text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="card">
        <p className="text-neutral-500 text-center py-8">No data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sign-in CTA for unauthenticated users */}
      {!isAuthenticated && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                <svg
                  className="h-5 w-5 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-primary-900 mb-1">
                  Save This Project
                </h3>
                <p className="text-sm text-primary-800">
                  Create a free account to access this project anytime and track your compliance progress
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowRegisterDialog(true)}
              className="btn-primary whitespace-nowrap"
            >
              Sign In to Save
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <JobStats stats={stats} />

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Diversion Gauge */}
        <DiversionGauge stats={stats} />

        {/* Material Breakdown */}
        <MaterialBreakdownChart summary={materials} />
      </div>

      {/* Refresh Indicator */}
      <div className="text-center">
        <button
          onClick={loadData}
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-primary-600 transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh data
        </button>
      </div>

      {/* Registration Dialog */}
      <RegisterPromptDialog
        isOpen={showRegisterDialog}
        onClose={() => setShowRegisterDialog(false)}
        projectId={jobId}
      />
    </div>
  );
}
